import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { filter, map, take } from 'rxjs/operators';
import { selectTenantInfo } from '../store/selectors';
import { CommunityFeatureFlags } from '../models/community/community-settings.model';
import { novaFeatures } from './models/nova-features';

/**
 * Refuses a route whose feature the school has switched off in Community Settings.
 *
 * Why this exists: hiding the nav entry is not the same as turning a feature off.
 * `events` was the proof - with the switch off, the sidebar entry and the mobile tab
 * disappeared, but `/community/events` and `/community/events/:id` stayed fully
 * reachable and fully WRITABLE by typing the URL, for admins and plain members alike.
 * RSVP buttons still worked. The switch's own copy promises the feature is off, so a
 * hidden-but-live route is a broken promise, not a shortcut.
 *
 * It composes with `communityAccessGuard` rather than replacing it: that guard answers
 * "may this person be in the community at all", this one answers "does this school
 * still have this feature". Both must pass, and the order in the `canActivate` array
 * does not matter because neither depends on the other's result.
 *
 * Deliberately reads the flag through `novaFeatures` rather than touching
 * `settings.features` directly - a missing flag means ON, and that rule is defined in
 * exactly one place. See the header of `models/nova-features.ts`.
 */
export function novaFeatureGuard(feature: keyof CommunityFeatureFlags): CanActivateFn {
  return () => {
    const store = inject(Store);
    const router = inject(Router);

    return store.pipe(
      select(selectTenantInfo),
      // The tenant arrives asynchronously. Deciding on `undefined` would read every
      // flag as ON (missing means ON) and let the route through on a cold load, which
      // is precisely the bug this guard exists to close.
      filter((tenant) => !!tenant),
      take(1),
      map((tenant) => {
        if (novaFeatures(tenant?.communitySettings)[feature]) {
          return true;
        }
        // Home, not /courses: the person is still entitled to the community, it is
        // only this one feature that is gone. Dropping them out of the module would
        // read as "you lost access".
        return router.createUrlTree(['/community/home']);
      })
    );
  };
}
